import { Controller } from 'stimulus'

const P = 'lexiqamus-user-guide-page'
const sel = (name) => `.${P}-${name}`

/*
 * Accessibility:
 * Manages accessibility and keyboard interactions for the LexiQamus user guide:
 * 1. Screenshot Lightbox: Opens modal images in a native accessible <dialog>,
 *    trapping focus properly and allowing users to close via the Escape key.
 * 2. Jump-Back Button: Helps keyboard and screen reader users easily return
 *    to their previous reading location after clicking an in-page anchor link.
 */
export default class extends Controller {
  static targets = ['lightbox', 'lightboxContent', 'jumpBack']

  connect () {
    this.jumpFrom = null
    this.jumpHost = null
    this.jumpTimer = null
    this.jumpTargetTimer = null
    if (this.hasLightboxTarget) {
      this.element.addEventListener('click', this.onScreenshotClick)
      this.lightboxTarget.addEventListener('click', this.onLightboxClick)
      this.lightboxTarget.addEventListener('close', this.onLightboxClosed)
    }

    if (this.hasJumpBackTarget) {
      document.addEventListener('keydown', this.onKeydown)
      document.addEventListener('click', this.onDismissJump, true)
      document.addEventListener('click', this.onAnchorClick)
      for (const ev of ['mousedown', 'wheel', 'touchstart']) {
        document.addEventListener(ev, this.onDismissJump, { passive: true, capture: true })
      }
    }
  }

  disconnect () {
    if (this.hasLightboxTarget) {
      this.element.removeEventListener('click', this.onScreenshotClick)
      this.lightboxTarget.removeEventListener('click', this.onLightboxClick)
      this.lightboxTarget.removeEventListener('close', this.onLightboxClosed)
    }

    if (this.hasJumpBackTarget) {
      document.removeEventListener('keydown', this.onKeydown)
      document.removeEventListener('click', this.onDismissJump, true)
      document.removeEventListener('click', this.onAnchorClick)
      for (const ev of ['mousedown', 'wheel', 'touchstart']) {
        document.removeEventListener(ev, this.onDismissJump, { capture: true })
      }
      this.detachJump()
    }
  }

  /*
   * Accessibility:
   * Handles opening screenshots in the full-screen lightbox modal.
   * The enlarge trigger is a native <button> element, allowing screen readers
   * and keyboard users (Enter / Space) to activate it naturally.
   * When opened in the native <dialog>, non-interactive elements are used so
   * keyboard users are not trapped on duplicate buttons inside the modal.
   */
  onScreenshotClick = (event) => {
    const trigger = event.target.closest('button.lexiqamus-user-guide-page-screenshot-enlarge')
    if (!trigger || !this.element.contains(trigger)) return
    const img = trigger.querySelector('img')
    if (!img || !this.hasLightboxTarget || !this.hasLightboxContentTarget) return

    const map = img.closest(sel('screenshot-map'))
    let node
    if (map) {
      // Overview figures: clone map + pins for the lightbox; unwrap enlarge buttons so the dialog is not interactive.
      node = map.cloneNode(true)
      node.querySelectorAll('button.lexiqamus-user-guide-page-screenshot-enlarge').forEach((btn) => {
        const cloneImg = btn.querySelector('img')
        btn.replaceWith(cloneImg)
      })
      const figure = map.closest('figure')
      if (figure) node.style.setProperty('--ar', figure.style.getPropertyValue('--ar'))
    } else {
      node = img.cloneNode(true)
    }

    this.lightboxContentTarget.replaceChildren(node)
    if (typeof this.lightboxTarget.showModal === 'function') {
      this.lightboxTarget.showModal()
    }
  }

  /*
   * Accessibility:
   * Closes the lightbox dialog when clicking on the outer backdrop.
   * Clicking inside the image content will not close the dialog.
   */
  onLightboxClick = (event) => {
    if (event.target === this.lightboxTarget && this.lightboxTarget.open) this.lightboxTarget.close()
  }

  /*
   * Accessibility:
   * Cleans up the lightbox image contents when the dialog is closed.
   * Using a native <dialog> ensures the browser automatically returns focus
   * to the button that originally opened the image, preserving focus order (WCAG 2.4.3).
   */
  onLightboxClosed = () => {
    if (this.hasLightboxContentTarget) this.lightboxContentTarget.replaceChildren()
  }

  /*
   * Accessibility:
   * Listens for the Escape key to dismiss the in-page jump-back button
   * without scrolling. If the lightbox dialog is currently open, this is skipped
   * so the native dialog can handle Escape itself.
   */
  onKeydown = (event) => {
    if (event.key !== 'Escape') return
    if (this.hasLightboxTarget && this.lightboxTarget.open) return
    this.jumpBack()
  }

  /*
   * Accessibility:
   * When an internal table of contents link or in-page anchor is clicked,
   * displays a floating "Jump back" button near the destination heading.
   * This provides a clear, accessible way for users to return to where they
   * were reading without having to manually scroll back up.
   */
  onAnchorClick = (event) => {
    const link = event.target.closest('a[href^="#"]')
    if (!link || !this.element.contains(link) || link.closest('.lexiqamus-page-toc, .lexiqamus-lq-backtop')) return

    const id = decodeURIComponent(link.getAttribute('href').slice(1))
    if (!id) return
    const target = document.getElementById(id)
    if (!target) return

    this.detachJump()
    this.jumpFrom = window.scrollY

    const head = target.querySelector('h2,h3,h4') ||
      (/^H[234]$/.test(target.tagName || '') ? target : (target.closest(sel('article')) || target))
    this.jumpHost = head
    this.jumpHost.classList.add(`${P}-jump-host`, `${P}-jump-target`)
    void this.jumpHost.offsetWidth
    this.jumpHost.appendChild(this.jumpBackTarget)
    this.jumpBackTarget.hidden = false
    requestAnimationFrame(() => this.jumpBackTarget.classList.add(`${P}-jump-back--visible`))
    this.autoHideJump()
    if (this.jumpTargetTimer) clearTimeout(this.jumpTargetTimer)
    this.jumpTargetTimer = setTimeout(() => {
      this.jumpHost?.classList.remove(`${P}-jump-target`)
      this.jumpTargetTimer = null
    }, 2400)
  }

  /*
   * Accessibility:
   * Dismisses the jump-back button if the user scrolls, clicks elsewhere,
   * or begins interacting with page content.
   */
  onDismissJump = (event) => {
    if (event?.target?.closest?.(sel('jump-back'))) return
    if (!this.jumpBackTarget.hidden) {
      this.jumpFrom = null
      this.detachJump()
    }
  }

  /*
   * Accessibility:
   * Smoothly scrolls the window back to the user's previous reading position
   * before following an in-page anchor link, then hides the jump-back button.
   */
  jumpBack () {
    if (this.jumpFrom == null) return
    window.scrollTo({ top: this.jumpFrom, behavior: 'smooth' })
    this.jumpFrom = null
    this.detachJump()
  }

  /*
   * Accessibility:
   * Automatically dismisses the jump-back button after 5 seconds of inactivity
   * to avoid permanently cluttering the reading area.
   */
  autoHideJump () {
    if (this.jumpTimer) clearTimeout(this.jumpTimer)
    this.jumpTimer = setTimeout(() => {
      this.jumpFrom = null
      this.detachJump()
    }, 5000)
  }

  /*
   * Accessibility:
   * Hides the jump-back button and cleans up any active timers or CSS highlight classes.
   */
  detachJump () {
    if (this.hasJumpBackTarget) {
      this.jumpBackTarget.classList.remove(`${P}-jump-back--visible`)
      this.jumpBackTarget.hidden = true
      if (this.jumpBackTarget.parentElement !== this.element) this.element.appendChild(this.jumpBackTarget)
    }
    if (this.jumpHost) this.jumpHost.classList.remove(`${P}-jump-host`, `${P}-jump-target`)
    if (this.jumpTimer) {
      clearTimeout(this.jumpTimer)
      this.jumpTimer = null
    }
    if (this.jumpTargetTimer) {
      clearTimeout(this.jumpTargetTimer)
      this.jumpTargetTimer = null
    }
  }
}
